import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { FiSearch, FiPlus, FiArrowLeft } from "react-icons/fi";
import { useDistribution } from "./useDistribution";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    title: "Add Channel",
    subtitle: "Browse available job boards and platforms",
    search_placeholder: "Search job boards...",
    setup_channel: "Setup Channel",
    no_channels_found: "No channels found matching",
    back: "Back",
  },
  fr: {
    title: "Ajouter un canal",
    subtitle: "Parcourir les sites d'emploi et plateformes disponibles",
    search_placeholder: "Rechercher des sites d'emploi...",
    setup_channel: "Configurer le canal",
    no_channels_found: "Aucun canal trouvé correspondant à",
    back: "Retour",
  },
};

export default function NewChannel({ job, posts, setPosts }) {
  const { t } = useComponentTranslation(translations);
  const navigate = useNavigate();
  const { availableChannels, loading, searchMarketplace, activateChannel } = useDistribution(job, posts, setPosts);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    searchMarketplace("");
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchMarketplace(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("..")} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <FiArrowLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{t("title")}</h2>
          <p className="text-sm text-gray-500">{t("subtitle")}</p>
        </div>
        <div className="relative w-72">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("search_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
          />
        </div>
      </div>

      {loading.market && availableChannels.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-5 bg-white border border-gray-100 rounded-xl h-44 animate-pulse">
              <div className="flex justify-between mb-4">
                <div className="w-32 h-5 bg-gray-200 rounded" />
                <div className="w-10 h-10 bg-gray-200 rounded-lg" />
              </div>
              <div className="h-4 bg-gray-100 rounded w-full mb-2" />
              <div className="h-4 bg-gray-100 rounded w-3/4 mb-4" />
              <div className="h-9 bg-gray-100 rounded-lg w-full" />
            </div>
          ))}
        </div>
      ) : availableChannels.length === 0 ? (
        <div className="py-12 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          {t("no_channels_found")} "{searchTerm}"
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableChannels.map((channel, idx) => (
            <div key={channel._id || idx} className="flex flex-col p-5 bg-white border border-gray-200 rounded-xl hover:border-primary/50 hover:shadow-md transition-all h-full">
              <div className="flex justify-between items-start gap-3 mb-3">
                <h4 className="font-bold text-gray-900 flex-1 min-w-0 truncate">{channel.name}</h4>
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center shrink-0 border border-gray-100 p-1">
                  {channel.logo ? (
                    <img src={channel.logo} alt={channel.name} className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-lg font-bold text-gray-400">{channel.name?.[0]}</span>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 line-clamp-2 mb-4 flex-1">{channel.description || ""}</p>
              <button
                onClick={() => activateChannel(channel)}
                className="w-full py-2 flex items-center justify-center gap-2 bg-gray-50 hover:bg-primary hover:text-white text-gray-700 rounded-lg font-medium text-sm transition-colors cursor-pointer"
              >
                <FiPlus className="w-4 h-4" />
                <span>{t("setup_channel")}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
